import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import PhotoLibraryOutlinedIcon from '@mui/icons-material/PhotoLibraryOutlined';

import type { LibraryItem } from '../../../domain';
import { libraryErrorMessage } from '../../../services/libraryService';
import {
  ACCEPT_ATTRIBUTE,
  MediaSourceService,
  sourceFromLibrary,
  validateSourceFile,
  type MediaKind,
  type MediaSource,
} from '../../../services/mediaSourceService';

export interface MediaSourcePickerProps {
  kind: MediaKind;
  /** What this input is, e.g. "Source still", "First frame", "Clip to extend". */
  label: string;
  value: MediaSource | null;
  onChange: (source: MediaSource | null) => void;
  disabled?: boolean;
}

/**
 * An Animation Lab input: upload a file, or choose one from your library — never a typed asset id.
 * Whatever is chosen becomes an owned asset id for the request, with a preview while it is chosen.
 * Files can also be dropped on the box.
 */
export const MediaSourcePicker: React.FC<MediaSourcePickerProps> = ({ kind, label, value, onChange, disabled }) => {
  const inputId = useId();
  const fileInput = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [dragging, setDragging] = useState(false);
  const lastFile = useRef<File | null>(null);
  const noun = kind === 'image' ? 'image' : 'video';

  const upload = useCallback(async (file: File) => {
    const problem = validateSourceFile(kind, file);
    if (problem) { setError(problem); return; }
    lastFile.current = file;
    setError('');
    setUploading(file.name);
    try {
      onChange(await MediaSourceService.upload(kind, file));
      lastFile.current = null;
    } catch (err) {
      setError(libraryErrorMessage(err, `The ${noun} could not be uploaded. Try again.`));
    } finally {
      setUploading(null);
    }
  }, [kind, noun, onChange]);

  const busy = disabled || uploading !== null;

  const onDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragging(false);
    if (busy) return;
    const file = event.dataTransfer.files?.[0];
    if (file) void upload(file);
  };

  return (
    <Box>
      <Typography variant="caption" component="label" htmlFor={inputId} sx={{ display: 'block', mb: 0.5, color: 'text.secondary', fontWeight: 600 }}>
        {label}
      </Typography>
      <input
        ref={fileInput}
        id={inputId}
        type="file"
        hidden
        accept={ACCEPT_ATTRIBUTE[kind]}
        data-testid={`media-source-input-${kind}`}
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void upload(file);
        }}
      />
      <Box
        role="group"
        aria-label={label}
        data-testid="media-source-picker"
        onDragOver={(e) => { e.preventDefault(); if (!busy) setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        sx={{
          border: '1px dashed',
          borderColor: dragging ? 'primary.main' : 'divider',
          bgcolor: dragging ? 'action.hover' : 'transparent',
          borderRadius: 2,
          p: 1.5,
          transition: 'border-color 120ms, background-color 120ms',
        }}
      >
        {uploading !== null ? (
          <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minHeight: 56 }} aria-live="polite">
            <CircularProgress size={22} />
            <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>Uploading {uploading}…</Typography>
          </Stack>
        ) : value ? (
          // The actions take their own row: the operations panel is narrow on desktop, and sharing a
          // line with them squeezed the name down to its first letter.
          <Stack spacing={1}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', sm: 'center' }}>
              <Preview kind={kind} source={value} />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="body2" noWrap title={value.label} data-testid="media-source-label">{value.label}</Typography>
                <Typography variant="caption" color="text.secondary" noWrap component="p">
                  {value.origin === 'upload' ? 'Uploaded' : value.origin === 'library' ? 'From your library' : 'From the gallery'}
                </Typography>
              </Box>
            </Stack>
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              <Button size="small" disabled={busy} onClick={() => fileInput.current?.click()}>Upload new</Button>
              <Button size="small" disabled={busy} onClick={() => setLibraryOpen(true)}>From library</Button>
              <Button size="small" color="inherit" disabled={busy} onClick={() => { setError(''); onChange(null); }}>Remove</Button>
            </Stack>
          </Stack>
        ) : (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<CloudUploadOutlinedIcon />}
              disabled={busy}
              onClick={() => fileInput.current?.click()}
            >
              Upload {noun}
            </Button>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<PhotoLibraryOutlinedIcon />}
              disabled={busy}
              onClick={() => setLibraryOpen(true)}
            >
              Choose from library
            </Button>
          </Stack>
        )}
        {!value && uploading === null && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {kind === 'image' ? 'PNG, JPEG or WebP' : 'MP4, WebM or MOV'}, up to 30 MB — or drop a file here.
          </Typography>
        )}
      </Box>
      {error && (
        <Alert
          severity="error"
          sx={{ mt: 1, py: 0 }}
          action={lastFile.current ? <Button color="inherit" size="small" onClick={() => lastFile.current && void upload(lastFile.current)}>Retry</Button> : undefined}
        >
          {error}
        </Alert>
      )}
      <LibraryPickerDialog
        open={libraryOpen}
        kind={kind}
        title={`Choose ${kind === 'image' ? 'an image' : 'a video'} — ${label.toLowerCase()}`}
        onClose={() => setLibraryOpen(false)}
        onPick={(item) => { setLibraryOpen(false); setError(''); onChange(sourceFromLibrary(item)); }}
      />
    </Box>
  );
};

function Preview({ kind, source }: { kind: MediaKind; source: MediaSource }) {
  const sx = { width: { xs: '100%', sm: 96 }, height: { xs: 160, sm: 64 }, objectFit: 'cover' as const, borderRadius: 1, bgcolor: 'action.hover', flexShrink: 0 };
  return kind === 'video' ? (
    <Box component="video" src={source.previewUrl} muted playsInline preload="metadata" sx={sx} data-testid="media-source-preview" />
  ) : (
    <Box component="img" src={source.previewUrl} alt="" sx={sx} data-testid="media-source-preview" />
  );
}

interface LibraryPickerDialogProps {
  open: boolean;
  kind: MediaKind;
  title: string;
  onClose: () => void;
  onPick: (item: LibraryItem) => void;
}

/** The person's Coral images or videos, newest first, paged. Picking one returns it. */
export const LibraryPickerDialog: React.FC<LibraryPickerDialogProps> = ({ open, kind, title, onClose, onPick }) => {
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(async (next: number) => {
    setLoading(true);
    setError('');
    try {
      const result = await MediaSourceService.listLibrary(kind, next);
      const fresh = result.items.filter((i) => i.kind === kind);
      setItems((current) => (next === 1 ? fresh : [...current, ...fresh]));
      setPage(next);
      setHasMore(result.hasMore);
    } catch (err) {
      setError(libraryErrorMessage(err, 'Your library could not be loaded.'));
    } finally {
      setLoading(false);
    }
  }, [kind]);

  useEffect(() => {
    if (open) void load(1);
  }, [open, load]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md" aria-labelledby="library-picker-title">
      <DialogTitle id="library-picker-title">{title}</DialogTitle>
      <DialogContent dividers>
        {error && <Alert severity="error" sx={{ mb: 2 }} action={<Button color="inherit" size="small" onClick={() => void load(page || 1)}>Retry</Button>}>{error}</Alert>}
        {!loading && !error && items.length === 0 && (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
            No {kind === 'image' ? 'images' : 'videos'} in your library yet. Upload one instead.
          </Typography>
        )}
        <Box
          data-testid="library-picker-grid"
          sx={{ display: 'grid', gap: 1.5, gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(3, 1fr)', md: 'repeat(4, 1fr)' } }}
        >
          {items.map((item) => (
            <ButtonBase
              key={item.id}
              onClick={() => onPick(item)}
              aria-label={`Use ${item.prompt?.trim() || (kind === 'image' ? 'this image' : 'this video')}`}
              sx={{ display: 'block', textAlign: 'left', borderRadius: 1.5, overflow: 'hidden', border: '1px solid', borderColor: 'divider', '&:hover, &:focus-visible': { borderColor: 'primary.main' } }}
            >
              {kind === 'video' ? (
                <Box component="video" src={item.url} poster={item.thumbnailUrl ?? undefined} muted playsInline preload="metadata" sx={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', display: 'block', bgcolor: 'action.hover' }} />
              ) : (
                <Box component="img" src={item.thumbnailUrl ?? item.url} alt="" loading="lazy" sx={{ width: '100%', aspectRatio: '1 / 1', objectFit: 'cover', display: 'block', bgcolor: 'action.hover' }} />
              )}
              <Typography variant="caption" noWrap sx={{ display: 'block', px: 1, py: 0.5 }}>{item.prompt || 'Untitled'}</Typography>
            </ButtonBase>
          ))}
        </Box>
        {loading && <Stack alignItems="center" sx={{ py: 2 }}><CircularProgress size={24} aria-label="Loading your library" /></Stack>}
      </DialogContent>
      <DialogActions>
        {hasMore && !loading && <Button onClick={() => void load(page + 1)}>Load more</Button>}
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
};
